import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Collection } from '../domain/cms/collection.model';
import { Content } from '../domain/cms/content.model';
import { ContentResponse } from '../domain/cms/content-response.model';
import { CreateContentRequest } from '../domain/cms/create-content-request.model';
import { UpdateContentRequest } from '../domain/cms/update-content-request.model';
import { Source } from '../domain/cms/source.model';
import { HttpUtils } from '../../../shared/utils/http-utils';

@Injectable()
export class ContentManagementService {

    constructor(private http: HttpClient) {
    }

    // CONTENT --------------------------------------------------------
    getContents(params): Observable<Content[]> {
        return this.http.get<Content[]>(`${environment.serviceUrl}/cms/cms/contents`, {
            params: HttpUtils.getHttpParams(params)
        });
    }

    getContentsPaging(params): Observable<ContentResponse> {
        return this.http.get<ContentResponse>(`${environment.serviceUrl}/cms/cms/contents/page`, {
            params: HttpUtils.getHttpParams(params)
        });
    }

    updateContent(updateContentRequest: UpdateContentRequest): Observable<any> {
        const formData: FormData = new FormData();
        Object.keys(updateContentRequest).forEach(prop => {
            if (updateContentRequest[prop]) {
                formData.append(prop, updateContentRequest[prop]);
            }
        });

        return this.http.post<any>(`${environment.serviceUrl}/cms/cms/contents/${updateContentRequest.id}/update`, formData);
    }

    createContent(createContentRequest: CreateContentRequest): Observable<any> {
        const formData: FormData = new FormData();
        formData.append('file', createContentRequest.file, createContentRequest.file.name);

        if (createContentRequest.posterFile) {
            formData.append('posterFile', createContentRequest.posterFile, createContentRequest.posterFile.name);
            formData.append('posterFileMime', createContentRequest.posterFileMime);
        }

        Object.keys(createContentRequest.content).forEach(prop => {
            if (prop === 'collections') {
                formData.append('collectionsJson', JSON.stringify(createContentRequest.content[prop]));
            } else {
                formData.append(prop, createContentRequest.content[prop]);
            }
        });

        return this.http.post<any>(`${environment.serviceUrl}/cms/cms/contents/upload`, formData);
    }

    deleteContents(contentsIds: string[]): Observable<any> {
        return this.http.post(`${environment.serviceUrl}/cms/cms/contents/delete`, contentsIds);
    }

    addContentToCollection(collection: Collection, contentsIds: string[]): Observable<any> {
        return this.http.put(`${environment.serviceUrl}/cms/cms/collections/${collection.id}/add-content`, contentsIds);
    }

    removeContentFromCollection(collection: Collection, contentsIds: string[]): Observable<any> {
        return this.http.put(`${environment.serviceUrl}/cms/cms/collections/${collection.id}/remove-content`, contentsIds);
    }
    // SOURCES ------------------------------------------------------------
    getSources(): Observable<Source[]> {
        return this.http.get<Source[]>(`${environment.serviceUrl}/cms/cms/sources`);
    }

    createSources(source: Source): Observable<Source> {
        const data = {
            name: source.name,
            description: source.description,
        };
        return this.http.post<Source>(`${environment.serviceUrl}/cms/cms/sources`, data);
    }

    updateSources(source: Source): Observable<any> {
        const data = {
            id: source.id,
            name: source.name,
            description: source.description,
        };
        return this.http.put(`${environment.serviceUrl}/cms/cms/source/${source.id}`, data);
    }

    deleteSource(source: Source): Observable<any> {
        return this.http.delete(`${environment.serviceUrl}/cms/cms/sources/${source.id}`);
    }
    // COLLECTIONS ----------------------------------------------------
    getCollections(): Observable<Collection[]> {
        return this.http.get<Collection[]>(`${environment.serviceUrl}/cms/cms/collections`);
    }

    getCollectionWithContent(collectionId: string): Observable<Collection> {
        return this.http.get<Collection>(`${environment.serviceUrl}/cms/cms/collections/${collectionId}`, {
            params: HttpUtils.getHttpParams({ includeContent: true })
        });
    }

    createCollection(collection: Collection): Observable<Collection> {
        const data = {
            description: collection.description,
            name: collection.name,
            externalUrl: collection.externalUrl
        };
        return this.http.post<Collection>(`${environment.serviceUrl}/cms/cms/collections`, data);
    }

    updateCollection(collection: Collection): Observable<any> {
        const data = {
            id: collection.id,
            description: collection.description,
            name: collection.name,
            externalUrl: collection.externalUrl
        };
        return this.http.put(`${environment.serviceUrl}/cms/cms/collections/${collection.id}`, data);
    }

    deleteCollection(collection: Collection): Observable<any> {
        return this.http.delete(`${environment.serviceUrl}/cms/cms/collections/${collection.id}`);
    }

    deleteCollections(collectionsIds: string[]): Observable<any> {
        return this.http.post(`${environment.serviceUrl}/cms/cms/collections/delete`, collectionsIds);
    }
}
